import { useCallback, useRef } from 'react'

/**
 * Сбор энтропии из движения пальца.
 *
 * Зачем это здесь, а не на сервере: пользователь должен реально влиять на
 * порядок карт, а не нажимать кнопку «перемешать». Траектория пальца —
 * координаты и тайминги — сворачивается в 256-битное число, которое уходит
 * на сервер вместе с выбором.
 *
 * Пакет не знает, что с этим числом делают дальше: для него это просто
 * дайджест жеста. Что оно становится client_seed в протоколе честной
 * тасовки — забота приложения.
 *
 * Честно о качестве энтропии: это НЕ криптографический источник случайности.
 * Траектория пальца предсказуема на пару бит и повторяема. Её роль другая —
 * гарантировать, что сервер не мог знать порядок карт заранее, потому что
 * он зафиксировал свой хеш ДО того, как палец коснулся экрана.
 * Непредсказуемость обеспечивает серверный сид, а не этот.
 */

export interface GestureEntropy {
  /** Записать точку. Дёшево: один push в массив. */
  record: (x: number, y: number) => void
  /** Свернуть накопленное в hex-дайджест SHA-256. */
  digest: () => Promise<string>
  /** Сколько точек накоплено — чтобы не отпустить пользователя слишком рано. */
  count: () => number
  reset: () => void
}

export function useGestureEntropy(): GestureEntropy {
  const points = useRef<number[]>([])
  const start = useRef<number>(0)

  const record = useCallback((x: number, y: number) => {
    if (start.current === 0) start.current = performance.now()
    // Округляем до целых: субпиксельные хвосты у разных устройств разные,
    // и их присутствие не добавляет энтропии, а мешает воспроизводимости.
    points.current.push(Math.round(x), Math.round(y), Math.round(performance.now() - start.current))
  }, [])

  const digest = useCallback(async () => {
    const data = new TextEncoder().encode(points.current.join(','))
    const hash = await crypto.subtle.digest('SHA-256', data)
    return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('')
  }, [])

  const count = useCallback(() => points.current.length / 3, [])

  const reset = useCallback(() => {
    points.current = []
    start.current = 0
  }, [])

  return { record, digest, count, reset }
}
